import { useState } from "react";
import { Link } from "react-router";
import { DownloadIcon } from "lucide-react";
import { useLayoutContext } from "@/components/Layout";
import {
  usePeriods,
  periodExportUrl,
} from "@/api/hooks/use-billing-statements";
import { LoadingSkeleton } from "@/components/LoadingSkeleton";
import { EmptyState } from "@/components/EmptyState";
import { ErrorState } from "@/components/ErrorState";
import { StatusBadge } from "@/components/StatusBadge";
import { formatUsd } from "@/lib/format";

const STATUS_OPTIONS = [
  { value: "", label: "All" },
  { value: "finalized", label: "Finalized" },
  { value: "draft", label: "Draft" },
] as const;

export function Statements() {
  const { tenantId } = useLayoutContext();
  const [statusFilter, setStatusFilter] = useState<string>("");
  const periodsQ = usePeriods(tenantId, statusFilter || undefined);

  if (!tenantId) {
    return <EmptyState message="Sign in to view statements" />;
  }

  if (periodsQ.isLoading) {
    return <LoadingSkeleton rows={6} />;
  }

  if (periodsQ.isError) {
    return (
      <ErrorState
        message={periodsQ.error?.message || "Failed to load statements"}
        onRetry={() => periodsQ.refetch()}
      />
    );
  }

  const periods = periodsQ.data?.periods ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold text-fg">Statements</h1>
        <div className="flex items-center gap-2">
          <label className="text-xs text-fg-muted" htmlFor="statement-status">
            Status
          </label>
          <select
            id="statement-status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded border border-line bg-canvas px-2 py-1 text-xs text-fg focus:outline-none focus:ring-1 focus:ring-electric-blue"
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="rounded-lg border border-line-subtle bg-surface">
        {periods.length === 0 ? (
          <div className="px-4 py-12 text-center text-sm text-fg-muted">
            No statements yet. Statements are created when a billing period closes.
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-line-subtle text-fg-muted">
                <th className="px-4 py-2.5 text-left font-medium">Period</th>
                <th className="px-4 py-2.5 text-left font-medium">Status</th>
                <th className="px-4 py-2.5 text-right font-medium">Baseline</th>
                <th className="px-4 py-2.5 text-right font-medium">Actual</th>
                <th className="px-4 py-2.5 text-right font-medium">Verified Savings</th>
                <th className="px-4 py-2.5 text-right font-medium">Invoice</th>
                <th className="px-4 py-2.5 text-right font-medium">Export</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line-subtle">
              {periods.map((p) => (
                <tr key={p.id} className="hover:bg-surface-2">
                  <td className="px-4 py-2.5">
                    <Link
                      to={`/statements/${p.id}`}
                      className="text-electric-blue hover:underline font-mono"
                    >
                      {p.period_start} – {p.period_end}
                    </Link>
                  </td>
                  <td className="px-4 py-2.5">
                    <StatusBadge status={p.status} />
                  </td>
                  <td className="px-4 py-2.5 text-right text-fg-muted font-mono">
                    {formatUsd(p.total_baseline_cost)}
                  </td>
                  <td className="px-4 py-2.5 text-right text-fg font-mono">
                    {formatUsd(p.total_actual_cost)}
                  </td>
                  <td className="px-4 py-2.5 text-right text-safe-green font-mono">
                    {formatUsd(p.total_verified_savings)}
                  </td>
                  <td className="px-4 py-2.5 text-right text-fg font-mono font-medium">
                    {formatUsd(p.total_invoice_amount)}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <div className="inline-flex items-center gap-2">
                      <DownloadButton href={periodExportUrl(p.id, "csv")} label="CSV" />
                      <DownloadButton href={periodExportUrl(p.id, "json")} label="JSON" />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function DownloadButton({ href, label }: { href: string; label: string }) {
  return (
    <a
      href={href}
      className="inline-flex items-center gap-1 rounded border border-line px-2 py-1 text-xs text-fg hover:bg-surface-2 transition-colors"
    >
      <DownloadIcon size={12} />
      {label}
    </a>
  );
}
